import React from "react";
import PropTypes from "prop-types";

// Components
import { Banner } from "components/Banner/Banner";
import { Text } from "components/Text/Text";

// Hooks
import { useLettersComposeReviewRecipientSender } from "./hooks/useLettersComposeReviewRecipientSender";

// Localization
import { useTranslation } from "localization/localization";

// Utils
import { handleKeyDown } from "utils/eventUtils";

export const LettersComposeReviewRecipientSender = (props) => {
    const {
        connected,
        hasAddedPostagePaidInSession,
        isEligibleForPostagePaid,
        loading,
        message,
        parentKey,
        recipient,
        sender,
        setRecipient,
        setShouldShowSenderModal,
    } = props;

    /**
     * Custom Hooks
     */

    const { t } = useTranslation();

    const {
        handleToggleContactModal,
        hasSenderAddress,
        isSender,
        senderCountryPretty,
    } = useLettersComposeReviewRecipientSender({
        parentKey,
        recipient,
        sender,
        setRecipient,
    });

    /**
     * End Hooks
     */

    function renderEditLink() {
        const dataSource = props[parentKey];
        const { connected, hasInitialAddress } = dataSource;
        const isRecipient = parentKey === "recipient";
        const editAction = isRecipient
            ? handleToggleContactModal
            : () => setShouldShowSenderModal(true);

        // Disable edit link if...
        // (a) User is not connected to recipient but does have an address
        // (b) Recipient is a Support Squad recipient
        if (
            (isRecipient && !connected && hasInitialAddress) ||
            (isRecipient && recipient.isSandboxxUser) ||
            (isRecipient && recipient.isSquadRecipient)
        ) {
            return (
                <span className="letters-compose__review__section__header__link link link-disabled">
                    {t("edit", { ns: "common" })}
                </span>
            );
        } else {
            return (
                <span
                    className="letters-compose__review__section__header__link link"
                    data-cy={`letters-compose-review-edit-${parentKey}`}
                    onClick={editAction}
                    onKeyDown={(e) => handleKeyDown(e, editAction)}
                    tabIndex="0"
                >
                    {t("edit", { ns: "common" })}
                </span>
            );
        }
    }

    function renderMaskedAddressText() {
        const { address, hasInitialAddress } = recipient;
        const hasAddress = !!address.line1;
        return (
            !connected &&
            hasAddress &&
            hasInitialAddress && (
                <div className="letters-compose__review__recipient-sender-masked-address-text">
                    <span className="letters-compose__review__recipient-sender-masked-address-text-text">
                        {t("recipient_address_masked", { ns: "letters" })}
                    </span>
                </div>
            )
        );
    }

    function renderRecipientSenderAddress(parentKey, dataSource) {
        const { address, connected, hasInitialAddress } = dataSource;
        const { line1, line2 } = address;
        const hasAddress = !!line1;
        const isRecipient = parentKey === "recipient";
        // Logic for masking address
        if (isRecipient && hasAddress && hasInitialAddress && !connected) {
            return (
                <>
                    <span>*****</span>
                    <span>*****</span>
                </>
            );
        }
        // Display address if it exists
        else if (hasAddress) {
            return (
                <>
                    <span>{line1}</span>
                    {line2 && <span>{line2}</span>}
                </>
            );
        }
        // Nothing returned if a user has no address, masked or otherwise
    }

    function renderRecipientSender() {
        const dataSource = props[parentKey];
        const addressDisplay = renderRecipientSenderAddress(
            parentKey,
            dataSource
        );
        const { address, connected, hasInitialAddress } = dataSource;
        const { city, state, zipcode } = address;
        const isRecipient = parentKey === "recipient";
        const header = isRecipient
            ? t("to", { ns: "common" })
            : t("from", { ns: "common" });
        const name = renderRecipientSenderName(parentKey, dataSource);
        const zipcodeDisplay =
            connected || !isRecipient || !hasInitialAddress ? zipcode : "*****";
        return (
            <div
                className="letters-compose__review__recipient-sender letters-compose__review__section"
                data-cy={`letters-compose-review-address-${isRecipient}`}
            >
                <div className="letters-compose__review__section__header">
                    <span className="letters-compose__review__section__header__text">
                        {header}
                    </span>
                    {renderEditLink()}
                </div>
                {!isRecipient && renderPostagePaidInvalidAddressBanner()}
                {name}
                {addressDisplay}
                {city && <span>{`${city}, ${state} ${zipcodeDisplay}`}</span>}
                {!isRecipient && senderCountryPretty && (
                    <span>{senderCountryPretty}</span>
                )}
                {isRecipient && renderMaskedAddressText()}
                {renderNoAddressBanner()}
            </div>
        );
    }

    function renderNoAddressBanner() {
        return (
            isSender &&
            !hasSenderAddress && (
                <Banner
                    classes="letters-compose__review__recipient-sender__banner-no-address"
                    config={{ shouldShowChevron: true, shouldShowIcon: true }}
                    onClick={() => setShouldShowSenderModal(true)}
                    theme="yellow"
                >
                    <Text isBold>
                        {t("whats_your_address", { ns: "letters" })}
                    </Text>
                    <Text>
                        {t("return_address_required", { ns: "letters" })}
                    </Text>
                </Banner>
            )
        );
    }

    function renderRecipientSenderName(parentKey, dataSource) {
        const { firstName, lastName, title } = dataSource;
        const { isNameReversed } = message;
        const isRecipient = parentKey === "recipient";
        const titleDisplay = title || "";

        if (isRecipient) {
            // If generating recipient name...
            const name = isNameReversed
                ? `${titleDisplay} ${lastName}, ${firstName}`
                : `${titleDisplay} ${firstName} ${lastName}`;
            return <span>{name}</span>;
        } else {
            // If generating sender name...
            return (
                <span
                    data-cy={`letters-compose-review-${parentKey}-name`}
                >{`${firstName} ${lastName}`}</span>
            );
        }
    }

    function renderPostagePaidInvalidAddressBanner() {
        return (
            hasAddedPostagePaidInSession &&
            !isEligibleForPostagePaid && (
                <Banner
                    config={{ shouldShowClose: true }}
                    classes="letters-compose__review__recipient-sender__banner"
                    loading={loading.addOns}
                    theme="yellow"
                >
                    <span>
                        {t("sender_address_ineligible_reply_postage", {
                            ns: "letters",
                        })}
                    </span>
                </Banner>
            )
        );
    }

    return renderRecipientSender();
};

LettersComposeReviewRecipientSender.propTypes = {
    connected: PropTypes.bool.isRequired,
    hasAddedPostagePaidInSession: PropTypes.bool.isRequired,
    isEditing: PropTypes.bool,
    isEligibleForPostagePaid: PropTypes.bool,
    loading: PropTypes.object.isRequired,
    message: PropTypes.object.isRequired,
    parentKey: PropTypes.string.isRequired,
    recipient: PropTypes.object.isRequired,
    sender: PropTypes.object.isRequired,
};
